(function () {
  'use strict';

  const SECTIONS = 'header-cart-link,product-add-to-cart,cart-drawer';
  let liveCart = { items: [] };

  function trackClarityEvent(eventName) {
    if (typeof window.clarity === 'function') {
      window.clarity('event', eventName);
    }
  }

  function formatPrice(amount) {
    const currency = window.Shopify?.currency?.active || (window.KIT_PATTERN && window.KIT_PATTERN.currency) || 'USD';
    const num = parseFloat(amount);
    if (Number.isNaN(num)) return '—';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(num);
    } catch (e) {
      return (Math.round(num * 100) / 100).toFixed(2);
    }
  }

  function getIcon(name) {
    try {
      if (window.KIT_ICONS && typeof window.KIT_ICONS[name] === 'string') return window.KIT_ICONS[name];
    } catch (e) {}
    const defaults = {
      lowStock: '<svg fill="none" class="text-red w-5 h-5" viewBox="0 0 24 24" stroke="#ff6b6b" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">\n          <circle cx="12" cy="12" r="10" stroke-width="2"></circle>\n          <path d="M12 8v4" stroke-width="2.5"></path>\n          <path d="M12 16h.01" stroke-width="2.5"></path>\n        </svg>',
      oos: '<svg fill="none" class="text-red w-5 h-5" viewBox="0 0 24 24" stroke="#ff6b6b" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">\n          <circle cx="12" cy="12" r="10" stroke-width="2"/>\n          <path d="M12 8v4" stroke-width="2.5"/>\n          <path d="M12 16h.01" stroke-width="2.5"/>\n        </svg>'
    };
    return defaults[name] || '';
  }

  function readStockTexts(card) {
    const el = card.querySelector('.kit-card-stock');
    if (!el) return {};
    return {
      oos: el.dataset.oosText,
      low: el.dataset.lowText,
      inStock: el.dataset.inStockText,
      max: el.dataset.maxText,
    };
  }

  function getStockStatus(available, qty, texts, maxedInCart) {
    texts = texts || {};
    if (maxedInCart) {
      return { msg: texts.max || 'Maximum quantity reached', cls: 'text-red-700 font-bold', icon: getIcon('oos') };
    }
    if (available === null || available === undefined) {
      return { msg: texts.inStock || 'In stock', cls: 'text-green-700', icon: '✓' };
    }
    if (available <= 0) {
      return { msg: texts.oos || 'Out of stock', cls: 'text-red-700 font-bold', icon: '' };
    }
    if (available < qty) {
      return { msg: (texts.low || 'Only __N__ in stock').replace('__N__', available), cls: 'text-red-700', icon: getIcon('lowStock') };
    }
    if (available < 50) {
      return { msg: (texts.low || 'Hurry, only __N__ left!').replace('__N__', available), cls: 'text-textBlack', icon: getIcon('lowStock') };
    }
    return { msg: texts.inStock || 'In stock', cls: 'text-green-700', icon: '✓' };
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) return { items: [] };
    return res.json();
  }

  let cartQtyByVariant = {};
  let cartFetchSeq = 0;

  async function refreshCartQtyMap() {
    const seq = ++cartFetchSeq;
    const cart = await fetchCart();

    if (seq !== cartFetchSeq) return liveCart;

    liveCart = cart;

    const map = {};

    (cart.items || []).forEach((item) => {
      const vid = String(item.variant_id);
      map[vid] = (map[vid] || 0) + item.quantity;
    });

    cartQtyByVariant = map;

    return cart;
  }

  let variantMaxCapMap = {};

  async function ensureVariantMaxCap(variantId) {
    const key = String(variantId || '');
    if (!/^\d+$/.test(key)) return null;
    if (key in variantMaxCapMap) return variantMaxCapMap[key];
    try {
      const res = await fetch(`/variants/${key}.json`);
      if (!res.ok) { variantMaxCapMap[key] = null; return null; }
      const data = await res.json();
      const rule = data?.variant?.quantity_rule;
      const max = rule && typeof rule.max === 'number' ? rule.max : null;
      variantMaxCapMap[key] = max;
      return max;
    } catch (e) {
      variantMaxCapMap[key] = null;
      return null;
    }
  }

  async function refreshPatternCartState(pattern) {
    if (!pattern) return;
    const cart = await fetchCart();
    pattern.patternInCart = (cart.items || []).some(
      (item) => String(item.variant_id) === String(pattern.variantId)
    );
  }

  function syncPatternCard(pattern) {
    const cards = document.querySelectorAll('.kit-pattern-card');
    if (!cards.length || !pattern) return;

    cards.forEach((card) => {
      const cartStatusEl = card.querySelector('.kit-pattern-card-cart-status');

      if (!cartStatusEl) return;

      if (pattern.ownsPattern) {
        // Pattern is already in the customer's digital library
        const inLibraryText = cartStatusEl.dataset.inLibraryText || 'Already in library';
        cartStatusEl.textContent = inLibraryText;
        cartStatusEl.href = '/a/library';
        cartStatusEl.classList.remove('hidden');
      } else if (pattern.patternInCart) {
        // Pattern is in the cart but not yet owned
        const inCartText = cartStatusEl.dataset.inCartText || 'Already added to cart';
        cartStatusEl.textContent = inCartText;
        cartStatusEl.href = '/cart';
        cartStatusEl.classList.remove('hidden');
      } else {
        cartStatusEl.classList.add('hidden');
      }
    });
  }

  async function verifyLibraryOwnership(pattern) {
    if (!pattern?.libraryApiUrl || !pattern?.sku) return false;
    try {
      const url = new URL(pattern.libraryApiUrl, window.location.origin);
      url.pathname += `/${pattern.sku}`;
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        redirect: 'follow',
      });
      if (!res.ok) return false;
      const result = await res.json();
      return Boolean(result?.data?.product?.content?.download_url);
    } catch (err) {
      return false;
    }
  }

  async function initPatternState(pattern) {
    if (!pattern) return;
    await refreshPatternCartState(pattern);
    if (pattern.isDownload && pattern.libraryApiUrl) {
      pattern.ownsPattern = await verifyLibraryOwnership(pattern);
    }
  }

  function shouldAddPatternToCart(pattern) {
    if (!pattern?.isDownload || !pattern?.variantId) return false;
    if (!pattern.isLoggedIn) return false;
    if (pattern.ownsPattern || pattern.patternInCart) return false;
    if (parseFloat(pattern.price || 0) === 0) return false;
    return true;
  }

  function shouldIncludePatternInTotal(pattern) {
    if (!pattern?.isDownload || !pattern?.variantId) return false;
    if (pattern.isLoggedIn && pattern.ownsPattern) return false;
    return true;
  }

  function initProject(projectEl, pattern) {
    const pid = projectEl.id;
    const modal = projectEl.querySelector('.kit-shade-modal');
    const modalGrid = modal?.querySelector('.kit-shade-modal-grid');
    const modalCurrent = modal?.querySelector('.kit-shade-modal-current');
    let modalReturnFocus = null;
    let activeCard = null;
    const disabledVariantIds = new Set();
    const selectedShadeBySlotIndex = {};

    function visibleCards() {
      return Array.from(projectEl.querySelectorAll('.kit-material-card')).filter((c) => !c.closest('.kit-size-group.hidden'));
    }

    function getCardRemaining(card) {
      const available = parseInt(card.dataset.available, 10) || 0;
      const vid = String(card.dataset.variantId || '');
      const inCart = cartQtyByVariant[vid] || 0;
      const ruleMax = variantMaxCapMap[vid];
      const inventoryCap = available >= 9999 ? Number.MAX_SAFE_INTEGER : available;
      const effectiveLimit = typeof ruleMax === 'number'
        ? Math.min(ruleMax, inventoryCap)
        : inventoryCap;
      return Math.max(0, effectiveLimit - inCart);
    }

    function getCardLimitQty(card) {
      const available = parseInt(card.dataset.available, 10) || 0;
      const vid = String(card.dataset.variantId || '');
      const ruleMax = variantMaxCapMap[vid];
      const inventoryCap = available >= 9999 ? Number.MAX_SAFE_INTEGER : available;
      return typeof ruleMax === 'number'
        ? Math.min(ruleMax, inventoryCap)
        : inventoryCap;
    }

    function cardIsReady(card) {
      if (card.dataset.resolved !== 'true') return false;
      if (disabledVariantIds.has(String(card.dataset.variantId))) return false;
      const selected = card.querySelector('.kit-card-checkbox') ? card.querySelector('.kit-card-checkbox').checked : true;
      if (!selected) return false;
      const qty = parseInt(card.dataset.slotQty, 10) || 0;
      const remaining = getCardRemaining(card);
      return qty > 0 && remaining >= qty;
    }

    function getCardMinQty(card) {
      return parseInt(card.dataset.minQty, 10) || parseInt(card.dataset.slotQty, 10) || 1;
    }

    function getCardMaxQty(card) {
      const remaining = getCardRemaining(card);
      const minQty = getCardMinQty(card);
      if (remaining >= 9999) return 50;
      if (remaining <= 0) return minQty;
      return Math.max(Math.min(remaining, 50), minQty);
    }

    function syncCardStockUI(card) {
      if (!card || card.dataset.resolved !== 'true') return;

      const minQty = getCardMinQty(card);
      const available = parseInt(card.dataset.available, 10) || 0;
      const remaining = getCardRemaining(card);
      const maxQty = getCardMaxQty(card);
      const vid = String(card.dataset.variantId || '');
      const selectedQty = parseInt(card.dataset.slotQty, 10) || getCardMinQty(card);
      const stockTexts = readStockTexts(card);
      const cartQty = cartQtyByVariant[vid] || 0;
      const limitQty = getCardLimitQty(card);

      const forcedOos = card.dataset.forceOos === 'true';
      const maxedInCart = forcedOos || (cartQty >= limitQty && limitQty > 0);
      const reachedLimit = maxedInCart;
      if (vid) {
        if (maxedInCart)
          disabledVariantIds.add(vid);
        else
          disabledVariantIds.delete(vid);
      }

      const inStock = remaining >= minQty && !maxedInCart;

      card.dataset.maxQty = String(maxQty);

      const qtyInput = card.querySelector('.kit-qty-input');
      if (qtyInput) {
        qtyInput.dataset.min = minQty;
        qtyInput.dataset.max = maxQty;
        let qty = parseInt(qtyInput.value, 10) || minQty;
        if (qty < minQty) qty = minQty;
        if (qty > maxQty) qty = maxQty;
        qtyInput.value = qty;
        card.dataset.slotQty = String(qty);
      }

      const qtyContainer = card.querySelector('.kit-qty_container');
      if (qtyContainer) {
        qtyContainer.classList.toggle('opacity-30', !inStock);
        qtyContainer.classList.toggle('pointer-events-none', !inStock);
      }

      const imgWrap = card.querySelector('.kit-card-image');
      if (imgWrap) imgWrap.classList.toggle('opacity-60', !inStock);

      card.classList.toggle('border-red-100', !inStock);
      card.classList.toggle('bg-red-50', !inStock);
      card.classList.toggle('border-gray-300', inStock);
      card.classList.toggle('bg-superLight', inStock);
      card.classList.toggle('hover:border-active', inStock);

      const infoCol = card.querySelector('.kit-card-info-col');
      if (infoCol) infoCol.classList.toggle('opacity-60', !inStock);

      const qtyRow = card.querySelector('.kit-card-qty-row');
      if (qtyRow) qtyRow.classList.toggle('opacity-60', !inStock);

      let stockEl = card.querySelector('.kit-card-stock');
      if (stockEl) {
        if (reachedLimit) {
          stockEl.className =
            'kit-card-stock text-sm flex items-center gap-1 justify-end md:justify-start text-red-700 font-bold';
          stockEl.innerHTML = `${getIcon('oos')} <p>${stockTexts.max || 'Maximum quantity reached'}</p>`;
        } else {
          const status = getStockStatus(remaining, selectedQty, stockTexts, false);
          stockEl.className = `kit-card-stock text-sm flex items-center gap-1 justify-end md:justify-start ${status.cls}`;
          stockEl.innerHTML = `${status.icon ? `${status.icon} ` : ''}<p>${status.msg}</p>`;
        }
      }

      const checkboxes = card.querySelectorAll('.kit-card-checkbox');

      checkboxes.forEach((cb) => {
        if (reachedLimit || !inStock) {
          cb.checked = false;
          cb.disabled = true;
          cb.classList.add('opacity-60');
        } else {
          cb.disabled = false;
          cb.classList.remove('opacity-60');
          if (cb.dataset.userUnchecked !== 'true') {
            cb.checked = true;
          }
        }
      });

      updateQtyButtons(card);
    }

    function resetCardSelectionState(card) {
      if (!card) return;
      card.classList.remove('opacity-50');
      const qtyRow = card.querySelector('.kit-card-qty-row');
      if (qtyRow) qtyRow.classList.remove('opacity-30');
      card.querySelectorAll('.kit-card-checkbox').forEach((cb) => {
        cb.checked = true;
        cb.disabled = false;
        cb.classList.remove('opacity-60');
      });
    }

    function updateSwatchImage(card, imageUrl) {
      if (!card || !imageUrl) return;

      const swatch = card.querySelector('.kit-color-swatch');
      if (swatch) {
        const swatchImg = swatch.querySelector('img');
        if (swatchImg) {
          swatchImg.src = imageUrl;
          swatchImg.alt = card.querySelector('.kit-card-variant-title')?.textContent?.trim() || '';
        }
      }

      const cardImg = card.querySelector('.kit-card-image img');
      if (cardImg) {
        cardImg.src = imageUrl;
        cardImg.srcset = imageUrl;
        cardImg.alt = card.querySelector('.kit-card-variant-title')?.textContent?.trim() || '';
      }
    }
    function checkComplete() {
      const msg = projectEl.querySelector('.kit-complete-msg');
      if (!msg) return;
      let allReady = true;
      visibleCards().forEach((card) => {
        if (!cardIsReady(card)) allReady = false;
      });
      msg.classList.toggle('hidden', !allReady || visibleCards().length === 0);
    }

    function updateAtcState() {
      const btn = projectEl.querySelector('.kit-atc-btn');
      if (!btn || btn.dataset.forceDisabled === 'true') return;

      let blocked = false;
      let hasSelectedReady = false;

      visibleCards().forEach((card) => {
        if (card.dataset.resolved !== 'true') return;
        const selected = card.querySelector('.kit-card-checkbox') ? card.querySelector('.kit-card-checkbox').checked : true;
        if (!selected) return;
        if (!cardIsReady(card)) {
          blocked = true;
          return;
        }
        hasSelectedReady = true;
      });

      if (!hasSelectedReady && !shouldAddPatternToCart(pattern)) blocked = true;

      btn.disabled = blocked;
      btn.setAttribute('aria-disabled', String(blocked));
      btn.classList.toggle('opacity-50', blocked);
      btn.classList.toggle('cursor-not-allowed', blocked);
      checkComplete();
    }

    async function setSize(size) {
      const selectedSize = String(size || '').trim();

      projectEl.querySelectorAll('.kit-size-tab').forEach((tab) => {
        const tabSize = String(tab.dataset.size || '').trim();
        const active = tabSize === selectedSize;
        tab.setAttribute('aria-pressed', String(active));
        tab.classList.toggle('border-active', active);
        tab.classList.toggle('bg-white', active);
        tab.classList.toggle('border-3', active);
        tab.classList.toggle('border-gray-300', !active);
        tab.classList.toggle('hover:border-active', !active);
      });

      projectEl.querySelectorAll('.kit-size-dropdown').forEach((sel) => {
        const val = String(sel.value || '').trim();
        if (val !== selectedSize) sel.value = selectedSize;
      });

      projectEl.querySelectorAll('.kit-size-group').forEach((grp) => {
        const groupSize = String(grp.dataset.size || '').trim();
        if (groupSize !== selectedSize) return;
        grp.querySelectorAll('.kit-material-card').forEach((card) => {
          const defaultQty = parseInt(card.dataset.minQty, 10) || parseInt(card.dataset.slotQty, 10) || 1;
          card.dataset.slotQty = String(defaultQty);
          const qtyInput = card.querySelector('.kit-qty-input');
          if (qtyInput) qtyInput.value = defaultQty;
        });
      });

      projectEl.querySelectorAll('.kit-size-group').forEach((grp) => {
        const groupSize = String(grp.dataset.size || '').trim();
        if (groupSize !== selectedSize) return;
        grp.querySelectorAll('.kit-material-card').forEach((card) => {
          const slotIndex = String(card.dataset.slotIndex || '');
          const saved = selectedShadeBySlotIndex[slotIndex];
          if (!saved?.variantId) return;
          const match = card.querySelector(
            `.kit-shade-grid-source .kit-shade-option[data-variant-id="${saved.variantId}"]`
          );
          if (!match) return;

          card.dataset.variantId = saved.variantId;
          delete card.dataset.forceOos;
          card.dataset.available = String(
            saved.available ?? match.dataset.available ?? ''
          );
          card.dataset.price = String(
            saved.price ?? match.dataset.price ?? ''
          );

          const titleEl = card.querySelector('.kit-card-variant-title');
          if (titleEl) {
            titleEl.textContent =
              saved.variantTitle || match.dataset.variantTitle || '';
          }

          const imgUrl = saved.image || match.dataset.image || '';
          const imgEl = card.querySelector('.kit-card-image img');
          if (imgEl && imgUrl) {
            imgEl.src = imgUrl;
            imgEl.srcset = imgUrl;
          }
          updateSwatchImage(card, imgUrl);

          const priceEl = card.querySelector('.kit-card-unit-price');
          if (priceEl) {
            priceEl.textContent = formatPrice(
              parseFloat(saved.price) || parseFloat(match.dataset.price) || 0
            );
          }

          card
            .querySelectorAll('.kit-shade-grid-source .kit-shade-option')
            .forEach((opt) => {
              const sel = opt.dataset.variantId === saved.variantId;
              opt.classList.toggle('border-primary', sel);
              opt.classList.toggle('ring-2', sel);
              opt.classList.toggle('ring-primary', sel);
              opt.classList.toggle('border-transparent', !sel);
            });

          syncCardStockUI(card);
        });
      });

      projectEl.querySelectorAll('.kit-size-group').forEach((grp) => {
        const groupSize = String(grp.dataset.size || '').trim();
        grp.classList.toggle('hidden', groupSize !== selectedSize);
      });

      closeModal();

      await refreshCartQtyMap();
      const allCards = Array.from(projectEl.querySelectorAll('.kit-material-card[data-resolved="true"]'));
      await Promise.all(allCards.map((c) => ensureVariantMaxCap(c.dataset.variantId)));
      allCards.forEach((card) => syncCardStockUI(card));

      recalcTotal();
      updateAtcState();
    }

    async function openModal(cardEl) {
      if (!modal || !modalGrid || !cardEl) return;
      await refreshCartQtyMap();
      const source = cardEl.querySelector('.kit-shade-grid-source');
      if (!source) return;

      const minQty = getCardMinQty(cardEl);

      const optIds = Array.from(source.querySelectorAll('.kit-shade-option'))
        .map((o) => String(o.dataset.variantId || '').trim())
        .filter(Boolean);
      if (optIds.length) await Promise.all(optIds.map((id) => ensureVariantMaxCap(id)));
      activeCard = cardEl;
      modalGrid.innerHTML = '';

      source.querySelectorAll('.kit-shade-option').forEach((opt) => {
        const clone = opt.cloneNode(true);
        if (!clone.querySelector('img') && clone.dataset.image) {
          const img = document.createElement('img');
          img.src = clone.dataset.image;
          img.alt = clone.getAttribute('aria-label') || clone.getAttribute('title') || '';
          img.className = 'w-full h-full object-cover';
          clone.appendChild(img);
        }
        const available = parseInt(clone.dataset.available, 10) || 0;
        const optVid = String(clone.dataset.variantId || '');
        const inCartQty = cartQtyByVariant[optVid] || 0;
        const remaining = available >= 9999 ? Number.MAX_SAFE_INTEGER : Math.max(0, available - inCartQty);
        const ok = remaining >= minQty;
        clone.disabled = !ok;
        clone.setAttribute('aria-disabled', String(!ok));
        // Mark the card's current shade so focus and screen readers start from it
        clone.setAttribute('aria-pressed', String(optVid === String(cardEl.dataset.variantId || '')));
        clone.classList.toggle('opacity-40', !ok);
        clone.classList.toggle('cursor-not-allowed', !ok);
        clone.classList.toggle('cursor-pointer', ok);
        ['after:absolute', 'after:content-[\'\']', 'after:w-[150%]', 'after:h-[2px]', 'after:bg-gray-800', 'after:top-1/2', 'after:left-1/2', 'after:-translate-x-1/2', 'after:-translate-y-1/2', 'after:-rotate-45'].forEach(c => clone.classList.toggle(c, !ok));
        modalGrid.appendChild(clone);
      });

      const titleEl = cardEl.querySelector('.kit-card-variant-title');
      if (modalCurrent) {
        modalCurrent.textContent = titleEl ? titleEl.textContent.trim() : '';
      }

      modalReturnFocus = cardEl.querySelector('.kit-edit-shade-btn');
      modal.classList.remove('hidden');
      modal.classList.add('flex');
      document.documentElement.classList.add('modal-open');
      // Move focus into the dialog so keyboard and screen reader users land in it
      (modalGrid.querySelector('.kit-shade-option[aria-pressed="true"]:not([disabled])')
        || modalGrid.querySelector('.kit-shade-option:not([disabled])')
        || modal.querySelector('.kit-shade-modal-close'))?.focus();
    }

    function closeModal() {
      if (!modal) return;
      const wasOpen = !modal.classList.contains('hidden');
      modal.classList.add('hidden');
      modal.classList.remove('flex');
      document.documentElement.classList.remove('modal-open');
      activeCard = null;
      if (modalGrid) modalGrid.innerHTML = '';
      if (wasOpen && modalReturnFocus?.isConnected) modalReturnFocus.focus();
      modalReturnFocus = null;
    }

    // Keep Tab inside the open dialog
    modal?.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusables = Array.from(modal.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    function applyShadeToCard(card, variantId, variantTitle, image, available, price) {
      card.dataset.variantId = variantId;
      card.dataset.available = available;
      card.dataset.price     = price;
      delete card.dataset.forceOos;

      const titleEl = card.querySelector('.kit-card-variant-title');
      if (titleEl) titleEl.textContent = variantTitle;

      const imgEl = card.querySelector('.kit-card-image img');
      if (imgEl && image) imgEl.src = image;

      updateSwatchImage(card, image);

      const priceEl = card.querySelector('.kit-card-unit-price');
      if (priceEl) priceEl.textContent = formatPrice(price);

      card.querySelectorAll('.kit-shade-grid-source .kit-shade-option').forEach((opt) => {
        const sel = opt.dataset.variantId === variantId;
        opt.classList.toggle('border-primary',     sel);
        opt.classList.toggle('ring-2',             sel);
        opt.classList.toggle('ring-primary',       sel);
        opt.classList.toggle('border-transparent', !sel);
      });

      syncCardStockUI(card);
    }

    function rememberSelectedShade(card, variantId, variantTitle, image, available, price) {
      const slotIndex = String(card?.dataset?.slotIndex || '');
      if (!slotIndex || !variantId) return;
      selectedShadeBySlotIndex[slotIndex] = {
        variantId: String(variantId),
        variantTitle: variantTitle || '',
        image: image || '',
        available: String(available ?? ''),
        price: String(price ?? ''),
      };
    }

    async function selectShade(cardEl, optionEl) {
      if (!optionEl || optionEl.disabled) return;

      trackClarityEvent('kit_builder_shade_change');
 
      const variantId      = optionEl.dataset.variantId;
      const variantTitle   = optionEl.dataset.variantTitle;
      const image          = optionEl.dataset.image;
      const available      = parseInt(optionEl.dataset.available, 10) || 0;
      const price          = parseFloat(optionEl.dataset.price) || 0;
      const minQty       = getCardMinQty(cardEl);

      await refreshCartQtyMap();
      await ensureVariantMaxCap(variantId);

      disabledVariantIds.delete(String(variantId));
      cardEl.dataset.slotQty = String(minQty);
      const qtyInput = cardEl.querySelector('.kit-qty-input');
      if (qtyInput) qtyInput.value = minQty;
      resetCardSelectionState(cardEl);

      applyShadeToCard(cardEl, variantId, variantTitle, image, available, price);
      rememberSelectedShade(cardEl, variantId, variantTitle, image, available, price);

      await refreshCartQtyMap();
      syncAllCards();

      closeModal();
      recalcTotal();
      updateAtcState();
    }

    function updateQtyButtons(card) {
      const input = card.querySelector('.kit-qty-input');
      const minus = card.querySelector('.kit-qty-minus');
      const plus = card.querySelector('.kit-qty-plus');
      if (!input) return;

      const val = parseInt(input.value, 10) || getCardMinQty(card);
      const min = parseInt(input.dataset.min, 10) || getCardMinQty(card);
      const max = parseInt(input.dataset.max, 10) || getCardMaxQty(card);

      if (minus) {
        minus.disabled = val <= min;
        minus.classList.toggle('opacity-30', val <= min);
        minus.classList.toggle('cursor-not-allowed', val <= min);
        minus.classList.toggle('cursor-pointer', val > min);
      }
      if (plus) {
        plus.disabled = val >= max;
        plus.classList.toggle('opacity-30', val >= max);
        plus.classList.toggle('cursor-not-allowed', val >= max);
        plus.classList.toggle('cursor-pointer', val < max);
      }
    }

    // Announce +/- changes, the buttons keep focus so nothing else tells screen readers the new value
    let qtyAnnounceTimeout = null;
    function announceQty(qty) {
      const status = projectEl.querySelector('.kit-qty-status');
      if (!status) return;
      clearTimeout(qtyAnnounceTimeout);
      status.textContent = '';
      qtyAnnounceTimeout = setTimeout(() => {
        status.textContent = status.dataset.quantityMessage.replace('[qty]', qty);
      }, 300);
    }

    function setCardQty(card, value, announce = false) {
      const input = card.querySelector('.kit-qty-input');
      if (!input) return;

      const min = getCardMinQty(card);
      const max = getCardMaxQty(card);
      let qty = parseInt(value, 10) || min;
      if (qty < min) qty = min;
      if (qty > max) qty = max;

      input.value = qty;
      // Limits live in data-* attributes: VoiceOver reads native min+max as a percentage
      input.dataset.min = min;
      input.dataset.max = max;
      card.dataset.slotQty = String(qty);
      updateQtyButtons(card);
      syncCardStockUI(card);
      recalcTotal();
      updateAtcState();
      if (announce) announceQty(qty);
      trackClarityEvent('kit_builder_qty_change');
    }

    function recalcTotal() {
      let total = 0;
      let hasResolved = false;

      if (shouldIncludePatternInTotal(pattern)) {
        total += parseFloat(pattern.price) || 0;
        hasResolved = true;
      }

      visibleCards().forEach((card) => {
        if (!cardIsReady(card)) return;
        const price = parseFloat(card.dataset.price) || 0;
        const qty = parseInt(card.dataset.slotQty, 10) || 0;
        total += price * qty;
        hasResolved = true;
      });

      const totalEl = projectEl.querySelector('.kit-total-price');
      if (totalEl) totalEl.textContent = hasResolved ? formatPrice(total) : '—';

      // Update price in ATC button
      const atcPriceSep = projectEl.querySelector('.kit-atc-price');
      const atcPriceVal = projectEl.querySelector('.kit-atc-price-value');
      if (atcPriceSep && atcPriceVal) {
        if (hasResolved) {
          atcPriceVal.textContent = formatPrice(total);
          atcPriceSep.classList.remove('hidden');
          atcPriceVal.classList.remove('hidden');
        } else {
          atcPriceSep.classList.add('hidden');
          atcPriceVal.classList.add('hidden');
        }
      }
    }

    function syncAllCards() {
      projectEl.querySelectorAll('.kit-material-card[data-resolved="true"]').forEach((card) => syncCardStockUI(card));
    }

    async function postCartItem(item) {
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [item],
            sections: SECTIONS,
            sections_url: document.location.pathname,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          return { ok: false, error: data.description || data.message || 'This item could not be added.' };
        }
        return { ok: true, data };
      } catch (e) {
        return { ok: false, error: e.message || 'Network error' };
      }
    }

    function hardDisableVariant(variantId) {
      const vid = String(variantId);
      disabledVariantIds.add(vid);
      projectEl.querySelectorAll('.kit-material-card').forEach((card) => {
        if (String(card.dataset.variantId) !== vid) return;
        card.dataset.forceOos = 'true';
        const cb = card.querySelector('.kit-card-checkbox');
        if (cb) { cb.checked = false; cb.disabled = true; cb.classList.add('opacity-60'); }
        const stockEl = card.querySelector('.kit-card-stock');
        if (stockEl) {
          const texts = readStockTexts(card);
          stockEl.className = 'kit-card-stock text-sm flex items-center gap-1 justify-end md:justify-start text-red-700 font-bold';
          stockEl.innerHTML = `${getIcon('oos')} <p>${texts.max || 'Maximum quantity reached'}</p>`;
        }
      });
    }

    async function addToCart() {
      const btn = projectEl.querySelector('.kit-atc-btn');
      if (!btn || btn.disabled) return;

      trackClarityEvent('kit_builder_add_to_cart');

      if (pattern?.isDownload && !pattern?.isLoggedIn) {
        const loginUrl = pattern.loginUrl || '/customer_authentication/login';
        const sep = String(loginUrl || '').indexOf('?') !== -1 ? '&' : '?';
        window.location.href = `${loginUrl}${sep}return_to=${encodeURIComponent(window.location.pathname + window.location.search + window.location.hash)}`;
        return;
      }

      await refreshPatternCartState(pattern);
      await refreshCartQtyMap();
      syncAllCards();
      updateAtcState();

      const items = [];
      let focusVariantId = null;
      let anySkipped = false;
      let patternIncluded = false;

      if (shouldAddPatternToCart(pattern)) {
        const patternItem = {
          id: Number(pattern.variantId),
          quantity: 1,
        };
        if (pattern.isDownload) {
          patternItem.properties = { __is_downloadable: 'true' };
        }
        items.push(patternItem);
        focusVariantId = pattern.variantId;
        patternIncluded = true;
      } else if (
        pattern.isDownload &&
        parseFloat(pattern.price || 0) === 0 &&
        pattern.isLoggedIn &&
        !pattern.ownsPattern &&
        pattern.libraryApiUrl &&
        pattern.sku
      ) {
        if (typeof window.handlePatternAction === 'function') {
           window.handlePatternAction('grant');
        } else {
           fetch(pattern.libraryApiUrl, {
             method: 'POST',
             headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
             redirect: 'follow',
             body: JSON.stringify({ sku: pattern.sku, source: 'storefront' })
           }).catch(e => console.error('Failed to auto-grant pattern:', e));
        }
        pattern.ownsPattern = true;
        syncPatternCard(pattern);
      } else if (
        pattern.isDownload &&
        pattern.variantId &&
        !pattern.isLoggedIn &&
        !pattern.ownsPattern &&
        !pattern.patternInCart &&
        window.showToast
      ) {
        window.showToast({
          message: 'Log in to add this digital pattern to your cart. Materials will still be added.',
          type: 'error',
        });
      }

      const checkedCards = [];
      visibleCards().forEach((card) => {
        if (card.dataset.resolved !== 'true') return;
        if (disabledVariantIds.has(String(card.dataset.variantId))) return;
        const selected = card.querySelector('.kit-card-checkbox') ? card.querySelector('.kit-card-checkbox').checked : true;
        if (!selected) return;
        checkedCards.push(card);
      });

      await Promise.all(checkedCards.map((card) => ensureVariantMaxCap(card.dataset.variantId)));
      checkedCards.forEach((card) => syncCardStockUI(card));

      checkedCards.forEach((card) => {
        if (disabledVariantIds.has(String(card.dataset.variantId))) { anySkipped = true; return; }
        const variantId = card.dataset.variantId;
        const requestedQty = parseInt(card.dataset.slotQty, 10) || 0;
        if (!variantId || requestedQty <= 0) return;

        const remaining = getCardRemaining(card);
        if (remaining <= 0) {
          anySkipped = true;
          return;
        }

        const willAdd = Math.min(requestedQty, remaining);
        if (willAdd < requestedQty) anySkipped = true;

        items.push({ id: Number(variantId), quantity: willAdd });
        if (!focusVariantId) focusVariantId = variantId;
      });

      if (!items.length) {
        updateAtcState();
        if (anySkipped && window.showToast) {
          window.showToast({ message: 'Those items are already at their maximum quantity in your cart.', type: 'error' });
        }
        return;
      }

      btn.disabled = true;

      try {
        let anyAdded = false;
        let lastSections = null;
        let patternAddSucceeded = false;

        for (const item of items) {
          const result = await postCartItem(item);
          if (result.ok) {
            anyAdded = true;
            if (result.data?.sections) lastSections = result.data.sections;
            if (patternIncluded && Number(item.id) === Number(pattern.variantId)) {
              patternAddSucceeded = true;
            }
          } else {
            anySkipped = true;
            hardDisableVariant(item.id, 'Maximum quantity reached');
            if (window.showToast) {
              window.showToast({ message: result.error, type: 'error' });
            }
          }
        }

        if (lastSections && typeof window.updateHeaderCartCount === 'function') {
          window.updateHeaderCartCount(lastSections);
        }

        if (patternAddSucceeded && lastSections) {
          if (window.CartDrawer?.refresh) window.CartDrawer.refresh(lastSections);
          if (typeof window.updateAddToCartButtons === 'function') {
            window.updateAddToCartButtons(lastSections);
          }
          if (typeof window.syncStickyAddToCartButton === 'function') {
            window.syncStickyAddToCartButton();
          }
        } else if (lastSections && window.CartDrawer?.refresh) {
          window.CartDrawer.refresh(lastSections);
        }

        const beforeQty = { ...cartQtyByVariant };

        await refreshPatternCartState(pattern);
        syncPatternCard(pattern);
        await refreshCartQtyMap();

        const afterQty = { ...cartQtyByVariant };

        let totalAdded = 0;

        items.forEach((item) => {
          const before = beforeQty[String(item.id)] || 0;
          const after = afterQty[String(item.id)] || 0;

          totalAdded += Math.max(0, after - before);
        });

        const wasAdded = totalAdded > 0;

        if (wasAdded && window.CartDrawer?.open) {
          await window.CartDrawer.open(
              focusVariantId ? Number(focusVariantId) : null
          );
        }
        syncAllCards();

        if (anyAdded && window.CartDrawer?.open) {
          await window.CartDrawer.open(focusVariantId ? parseInt(focusVariantId, 10) : null);
        }

        if (anyAdded && anySkipped && window.showToast) {
          window.showToast({ message: 'Added what we could — one or more items were already at their cart limit.', type: 'info' });
        }

        recalcTotal();
      } catch (err) {
        if (window.showToast) {
          window.showToast({ message: err.message || 'Unable to add items', type: 'error' });
        }
      } finally {
        updateAtcState();
      }
    }

    function bind() {
      projectEl.addEventListener('click', (e) => {
        const tab = e.target.closest('.kit-size-tab');
        if (tab && projectEl.contains(tab)) {
          void setSize(tab.dataset.size);
          return;
        }

        const editBtn = e.target.closest('.kit-edit-shade-btn');
        if (editBtn && projectEl.contains(editBtn)) {
          const cardEl = editBtn.closest('.kit-material-card');
          if (cardEl) void openModal(cardEl);
          return;
        }

        const closeBtn = e.target.closest('.kit-shade-modal-close');
        if (closeBtn && projectEl.contains(closeBtn)) {
          closeModal();
          return;
        }

        const backdrop = e.target.closest('.kit-shade-modal-backdrop');
        if (backdrop && projectEl.contains(backdrop)) {
          closeModal();
          return;
        }

        const shadeOpt = e.target.closest('.kit-shade-modal-grid .kit-shade-option');
        if (shadeOpt && activeCard && !shadeOpt.disabled) {
          selectShade(activeCard, shadeOpt);
          return;
        }

        const minus = e.target.closest('.kit-qty-minus');
        if (minus && projectEl.contains(minus)) {
          const card = minus.closest('.kit-material-card');
          const input = card?.querySelector('.kit-qty-input');
          if (card && input) setCardQty(card, (parseInt(input.value, 10) || 1) - 1, true);
          return;
        }

        const plus = e.target.closest('.kit-qty-plus');
        if (plus && projectEl.contains(plus)) {
          const card = plus.closest('.kit-material-card');
          const input = card?.querySelector('.kit-qty-input');
          if (card && input) setCardQty(card, (parseInt(input.value, 10) || 1) + 1, true);
        }
      });

      projectEl.querySelectorAll('.kit-size-dropdown').forEach((sel) => {
        sel.addEventListener('change', (e) => void setSize(e.target.value));
      });

      projectEl.querySelectorAll('.kit-size-guide-toggle').forEach((btn) => {
        btn.addEventListener('click', () => {
          const guide = projectEl.querySelector('.kit-size-guide');
          if (guide) guide.classList.toggle('hidden');
        });
      });

      projectEl.querySelectorAll('.kit-qty-input').forEach((input) => {
        input.addEventListener('change', () => {
          const card = input.closest('.kit-material-card');
          if (card) setCardQty(card, input.value);
        });
        input.addEventListener('blur', () => {
          const card = input.closest('.kit-material-card');
          if (card) setCardQty(card, input.value);
        });
      });

      projectEl.querySelectorAll('.kit-card-checkbox').forEach((input) => {
        input.addEventListener('change', () => {
          const card = input.closest('.kit-material-card');
          if (!card) return;
          const checked = input.checked;
          const other = card.querySelectorAll('.kit-card-checkbox');
          other.forEach((box) => { if (box !== input) box.checked = checked; });
          const qtyRow = card.querySelector('.kit-card-qty-row');
          if (qtyRow) qtyRow.classList.toggle('opacity-30', !checked);
          card.classList.toggle('opacity-50', !checked);
          input.dataset.userUnchecked = checked ? 'false' : 'true';
          recalcTotal();
          updateAtcState();
        });
      });

      projectEl.querySelectorAll('.kit-material-card[data-resolved="true"]').forEach((card) => {
        syncCardStockUI(card);
      });

      const atcBtn = projectEl.querySelector('.kit-atc-btn');
      if (atcBtn) {
        if (atcBtn.hasAttribute('disabled') && atcBtn.getAttribute('aria-disabled') === 'true') {
          atcBtn.dataset.forceDisabled = 'true';
        }
        atcBtn.addEventListener('click', addToCart);
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal && !modal.classList.contains('hidden')) {
          closeModal();
        }
      });
    }

    bind();

    projectEl._kitRecalc = () => {
      syncAllCards();
      recalcTotal();
      updateAtcState();
    };

    const initialTab = projectEl.querySelector('.kit-size-tab[aria-pressed="true"]');
    const initialDropdown = projectEl.querySelector('.kit-size-dropdown');
    const initialSize = initialTab
      ? initialTab.dataset.size
      : initialDropdown
        ? initialDropdown.value
        : null;

    if (initialSize) {
      setSize(initialSize);
    } else {
      visibleCards().forEach((card) => syncCardStockUI(card));
      recalcTotal();
      updateAtcState();
    }
  }

  async function boot() {
    const pattern = window.KIT_PATTERN || null;
    await initPatternState(pattern);
    await refreshCartQtyMap();

    const allVariantIds = new Set();
    document.querySelectorAll('.kit-material-card[data-variant-id]').forEach((card) => {
      allVariantIds.add(card.dataset.variantId);
    });
    await Promise.all([...allVariantIds].map((id) => ensureVariantMaxCap(id)));

    syncPatternCard(pattern);

    const projects = document.querySelectorAll('.pattern-kit-project');
    projects.forEach((projectEl) => initProject(projectEl, pattern));
  }

  window.syncKitPatternCart = async function () {
    const pattern = window.KIT_PATTERN;
    if (!pattern) return;
    await refreshPatternCartState(pattern);
    await refreshCartQtyMap();
    syncPatternCard(pattern);

    document.querySelectorAll('.pattern-kit-project').forEach((el) => {
      if (typeof el._kitRecalc === 'function') el._kitRecalc();
    });
  };

  ['cart:refresh', 'cart:change', 'cart-update', 'on:cart:open'].forEach((ev) => {
    window.addEventListener(ev, () => {
      window.syncKitPatternCart();
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
